import { Trash2 } from 'lucide-react';
import { ComponentProps, PropsWithChildren, ReactNode } from 'react';

import { AutomationTestingProps } from '@/types';
import { confirmStackDelete } from '@/react/common/stacks/common/confirm-stack-remove';

import { LoadingButton } from '../LoadingButton';
import { Button } from '../Button';

type ConfirmOrClick =
  | {
      confirmMessage: ReactNode;
      onConfirmed(removeVolumes: boolean): Promise<void> | void;
      onClick?: never;
    }
  | {
      confirmMessage?: never;
      onConfirmed?: never;
      /** if onClick is set, will skip confirmation (confirmation should be done on the parent) */
      onClick(): void;
    };

export function DeleteButtonWithRemoveVolume({
  disabled,
  size,
  children,
  isLoading,
  loadingText = 'Removing',
  'data-cy': dataCy,
  ...props
}: PropsWithChildren<
  AutomationTestingProps &
    ConfirmOrClick & {
      size?: ComponentProps<typeof Button>['size'];
      disabled?: boolean;
      isLoading?: boolean;
      loadingText?: string;
    }
>) {
  if (isLoading === undefined) {
    return (
      <Button
        size={size}
        color="dangerlight"
        disabled={disabled || isLoading}
        onClick={() => handleClick()}
        icon={Trash2}
        className="!m-0"
        data-cy={dataCy}
      >
        {children || 'Remove'}
      </Button>
    );
  }

  return (
    <LoadingButton
      size={size}
      color="dangerlight"
      disabled={disabled}
      onClick={() => handleClick()}
      icon={Trash2}
      className="!m-0"
      data-cy={dataCy}
      isLoading={isLoading}
      loadingText={loadingText}
    >
      {children || 'Remove'}
    </LoadingButton>
  );

  async function handleClick() {
    const { onConfirmed, onClick } = props;
    if (onClick) {
      return onClick();
    }

    const title = 'Are you sure you want to remove this stack?';
    const result = await confirmStackDelete(title);

    if (!result) {
      return undefined;
    }
    return onConfirmed(result?.RemoveVolumes);
  }
}
